import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { ChecklistModel } from '@fi-app/checklist/checklist.model';
import {
    IChecklistReportBusinessUnitData,
    IChecklistReportCheckpointsData,
    IChecklistReportSelectionData,
    ICheckpointReportData,
} from '../../checklist-report';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { ChecklistReportService } from '../../checklist-report.service';
import { AppTextService } from '@fi-shared/app-text';
import { PublicationType } from '@fi-shared/types/pubication-type';
import { UserPreferencesService } from '@fi-shared/fi-shared-services/user-preferences.service';
import { propComparator } from '@fi-shared/utils/array-sort-comparators';
import { ChecklistReportHelperService } from '../../checklist-report-helper.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { CheckpointStatusType } from '@fi-shared/checklist';
import { FiImageSize } from '@fi-shared/types';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { UserService } from '@fi-app/user/user.service';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';

@Component({
    selector: 'fi-checklist-report-checkpoints-on-unit',
    templateUrl: './checklist-report-checkpoints-on-unit.component.html',
    styleUrls: ['./checklist-report-checkpoints-on-unit.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class ChecklistReportCheckpointsOnUnitComponent implements OnInit, OnChanges {
    @Input() checklist: ChecklistModel;
    @Input() selectionData: IChecklistReportSelectionData;
    @Input() businessUnitsData: IChecklistReportBusinessUnitData[];
    @Input() unitId: number;
    @Input() status: any;
    private statusHeaderTextIds: any;

    isLoaded = false;
    unitName: string;
    checkpointsData: IChecklistReportCheckpointsData;
    reverseTable: boolean;
    FiStateNames = FiStateNames;
    FiImageSize = FiImageSize;
    itemsListType = FiItemsListType.ReportViewCheckpointsOnUnit;

    constructor(
        private checklistReportsService: ChecklistReportService,
        private appTextService: AppTextService,
        private userPreferencesService: UserPreferencesService,
        private checklistReportHelperService: ChecklistReportHelperService,
        private loadingService: LoadingService,
        private detectDeviceService: DetectDevicesService,
        private userService: UserService,
        private fiFormatDateService: FiFormatDateService,
    ) {
        this.statusHeaderTextIds = {};
        this.statusHeaderTextIds[ChecklistModel.Status.Ok] = 'checklist_reports.checkpoints_on_unit_header_ok';
        this.statusHeaderTextIds[ChecklistModel.Status.Deviation] =
            'checklist_reports.checkpoints_on_unit_header_deviation';
        this.statusHeaderTextIds[ChecklistModel.Status.Failed] = 'checklist_reports.checkpoints_on_unit_header_failed';
        this.statusHeaderTextIds[ChecklistModel.Status.OkCheckpoints] =
            'checklist_reports.checkpoints_on_unit_header_ok';
        this.statusHeaderTextIds[ChecklistModel.Status.DeviationCheckpoints] =
            'checklist_reports.checkpoints_on_unit_header_deviation';
    }

    ngOnInit() {
        this.reverseTable = this.userPreferencesService.getListSort(this.itemsListType) || false;

        this.loadData();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const change = changes.unitId;
        if (change && !change.firstChange) {
            this.loadData();
        }
    }

    getStatusHeaderText() {
        return this.appTextService.get(this.statusHeaderTextIds[this.status], {
            // '{status}':  this.reportViewCtrl.getSelectedStatusName(),
            '{unit}': this.unitName,
        });
    }

    getActionText(checkpoint: ICheckpointReportData) {
        if (checkpoint.IsNotApplicable)
            return this.appTextService.get(
                checkpoint.ChangedByUserId ? 'checklist_reports.not_applicable_by' : 'checklist.not_applicable',
            );

        return this.appTextService.get(
            checkpoint.Answer === CheckpointStatusType.Ok || checkpoint.Answer === CheckpointStatusType.NotOk
                ? 'checklist_reports.checked_by'
                : '',
        );
    }

    getCommentContent(checkpoint: ICheckpointReportData) {
        if (checkpoint.Comment && checkpoint.Comment.Content) return checkpoint.Comment.Content;
        if (checkpoint.Answer === CheckpointStatusType.NotOk)
            return this.appTextService.get('checklist_reports.deviation_without_comments');
        return '';
    }

    getPeriodDates(period) {
        let result = '<span>' + this.fiFormatDateService.formatDate(period.StartDateTime, 'date') + '</span>';
        if (
            this.checklist.TimeSetting === ChecklistModel.TimeSettings.Recurring &&
            this.checklist.RepeatFrequency === ChecklistModel.RepeatFrequency.Daily
        ) {
            result += ' <span>' + this.fiFormatDateService.formatDate(period.StartDateTime, 'time') + '</span> - ';
            if (
                this.fiFormatDateService.formatDate(period.StartDateTime, 'date') !==
                this.fiFormatDateService.formatDate(period.EndDateTime, 'date')
            ) {
                result += '<span>' + this.fiFormatDateService.formatDate(period.EndDateTime, 'date') + '</span> ';
            }
            result += this.fiFormatDateService.formatDate(period.EndDateTime, 'time');
        } else if (
            this.checklist.TimeSetting === ChecklistModel.TimeSettings.OneTime &&
            this.checklist.PublicationSetting.PublicationType === PublicationType.Continous
        ) {
            result += ' - <span>' + this.appTextService.get('common.unlimited') + '</span>';
        } else {
            result += ' - ' + '<span>' + this.fiFormatDateService.formatDate(period.EndDateTime, 'date') + '</span>';
        }

        return result;
    }

    isShowCommentAndVideoColumn() {
        return this.status != ChecklistModel.Status.Failed;
    }

    tableSortClick($event) {
        $event.preventDefault();
        this.reverseTable = !this.reverseTable;
        this.sortTable();
        this.userPreferencesService.saveListSort(FiItemsListType.ReportViewCheckpointsOnUnit, this.reverseTable);
    }

    private sortTable() {
        this.checkpointsData.Periods.sort(propComparator('StartDateTime', this.reverseTable));
        this.checkpointsData.Periods.forEach((period) => period.CheckPoints.sort(propComparator('CheckPointName')));
    }

    userNameClick($event, checkpoint: ICheckpointReportData) {
        $event.preventDefault();
        this.userService.openUserViewDialog(checkpoint.ChangedByUserId);
    }

    private loadData() {
        this.loadingService.trackPromise(
            this.checklistReportsService
                .GetCheckListUnitReportDetails(
                    this.checklist.Id,
                    this.selectionData.StartDate,
                    this.selectionData.EndDate,
                    this.unitId,
                    this.status,
                )
                .then((data) => {
                    this.checkpointsData = data;
                    this.sortTable();
                    this.isLoaded = true;
                    this.unitName = this.getUnitName();
                    this.checklistReportHelperService.raiseCheckpointsOnUnitLoadedEvent();
                }),
        );
    }

    private getUnitName() {
        const businessUnitData = this.businessUnitsData;
        for (let i = 0; i < businessUnitData.length; i++) {
            if (businessUnitData[i].ObjectType === ObjectTypes.BusinessUnit && businessUnitData[i].Id == this.unitId) {
                return businessUnitData[i].Name;
            }
            if (businessUnitData[i].ChildItems) {
                let childItems = businessUnitData[i].ChildItems;
                for (let j = 0; j < childItems.length; j++) {
                    if (childItems[j].ObjectType === ObjectTypes.BusinessUnit && childItems[j].Id == this.unitId) {
                        return childItems[j].Name;
                    }
                }
            }
        }
    }

    isMobilePhoneSmall() {
        return this.detectDeviceService.isMobileSmall();
    }

    getAttachmentSize() {
        return this.isMobilePhoneSmall() ? FiImageSize.Thumbnail : FiImageSize.Small;
    }
}
